import React, { useState, useEffect } from "react";
import { Badge, Tabs, Tab, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWallet,
  FaInfoCircle,
  FaFileInvoiceDollar,
  FaHistory,
  FaGlobe,
  FaIdCard,
  FaCreditCard,
  FaCalendarAlt,
  FaTimes,
  FaShoppingCart,
} from "react-icons/fa";

import BrandTable from "../core/components/BrandTable/BrandTable";
import { getAllDuePurchasesForSupplierId } from "../../redux/features/purchase.features";
import apiService from "../../services/apiServices";

const SupplierDetails = ({ supplier, onClose }) => {
  const [activeTab, setActiveTab] = useState("identity");
  const [imageError, setImageError] = useState(false);
  const [liveAccount, setLiveAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const dispatch = useDispatch();
  const { supplierDuePurchases, status } = useSelector((state) => state.purchaseReducer);

  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  // Fetch purchase history and account when component mounts or supplier changes
  useEffect(() => {
    const refreshData = async () => {
      if (supplier?._id) {
        dispatch(getAllDuePurchasesForSupplierId(supplier._id));

        // Fetch live account balance
        try {
          setAccountLoading(true);
          const response = await apiService.get(`accounts/owner/Supplier/${supplier._id}`);
          setLiveAccount(response.data);
          setAccountLoading(false);
        } catch (err) {
          console.error("Failed to fetch live account:", err);
          setAccountLoading(false);
        }
      }
    };

    refreshData();
  }, [supplier?._id, dispatch]);

  if (!supplier) return null;

  // Purchase history table columns
  const purchaseColumns = [
    {
      key: "purchaseDate",
      label: "Purchase Date",
      sortable: true,
      render: (val) => <span className="small">{new Date(val).toLocaleDateString()}</span>,
    },
    {
      key: "referenceNumber",
      label: "Reference #",
      sortable: true,
      render: (val) => <Badge bg="light" text="dark" className="border shadow-sm font-monospace">{val}</Badge>,
    },
    {
      key: "total_items_count",
      label: "Items",
      align: "center",
      render: (val) => <span className="fw-bold text-primary">{val}</span>,
    },
    {
      key: "grand_total",
      label: "Grand Total",
      align: "right",
      sortable: true,
      render: (val) => <span className="fw-bold text-success font-monospace">Rs. {val?.toLocaleString()}</span>,
    },
    {
      key: "payment_status",
      label: "Payment",
      render: (val) => {
        const statusColors = {
          "Paid": "success",
          "Partial": "warning",
          "Not Paid": "danger",
        };
        return <Badge bg={statusColors[val] || "secondary"} className="x-small">{val}</Badge>;
      },
    },
    {
      key: "purchase_status",
      label: "Status",
      render: (val) => {
        const statusColors = {
          "Received": "success",
          "Pending": "warning",
          "Cancelled": "secondary",
        };
        return <Badge bg={statusColors[val] || "secondary"} className="x-small">{val}</Badge>;
      },
    },
    {
      key: "payment_due_amount",
      label: "Due Amount",
      align: "right",
      render: (val) => val > 0 ? (
        <span className="fw-bold text-danger font-monospace">Rs. {val?.toLocaleString()}</span>
      ) : (
        <span className="text-muted small">-</span>
      ),
    },
  ];

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100 bg-white" style={{ zIndex: 99999, overflow: "auto" }}>

      <style>
        {`
          .details-label { font-size: 0.8rem; color: #8592a3; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 4px; }
          .details-value { font-size: 1.1rem; color: #566a7f; font-weight: 500; }
          .spec-card { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 12px; padding: 16px; transition: all 0.2s; }
          .spec-card:hover { border-color: #696cff; background: #fff; box-shadow: 0 4px 12px rgba(105, 108, 255, 0.08); }
          .image-glow { position: relative; }
          .image-glow::after { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.05); pointer-events: none; border-radius: 16px; }
          .intel-tabs .nav-link {
            padding: 1.25rem 2rem;
            font-weight: 600;
            border: none;
            color: #697a8d;
            display: flex;
            align-items: center;
            gap: 10px;
            transition: all 0.3s;
          }
          .intel-tabs .nav-link.active {
            color: #696cff!important;
            background: #fff!important;
            border-bottom: 3px solid #696cff!important;
            box-shadow: 0 4px 10px rgba(105, 108, 255, 0.1);
          }
          .intel-tabs .nav-link:hover:not(.active) { background: #fdfdff; color: #696cff; }
          .financial-card { 
            background: linear-gradient(135deg, #696cff 0%, #5a5fd7 100%);
            border-radius: 16px;
            padding: 24px;
            color: white;
            position: relative;
            overflow: hidden;
          }
          .financial-card::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
            pointer-events: none;
          }
        `}
      </style>

      {supplier && (
        <div className="container-fluid p-0">
          {/* Sticky Header with Close Button */}
          <div className="row g-0 border-bottom bg-white sticky-top shadow-sm px-4 py-3" style={{ zIndex: 1000 }}>
            <div className="col-12 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <h4 className="mb-0 fw-bold">{supplier.business_name}</h4>
                <Badge bg="primary" className="shadow-sm font-monospace">ID: {supplier.supplier_id}</Badge>
                <span className="text-muted small">|</span>
                <span className="text-muted small fw-bold text-uppercase">Supplier</span>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <div className="text-end">
                  <div className="text-muted x-small fw-bold text-uppercase">Account Balance</div>
                  <div className={`fw-bold ${accountLoading ? 'opacity-50' : 'text-danger'}`}>
                    {accountLoading ? (
                      <Spinner animation="border" size="sm" variant="danger" className="me-2" />
                    ) : (
                      <>Rs. {(liveAccount?.balance || supplier.account?.balance || supplier.financial?.current_balance || 0).toLocaleString()}</>
                    )}
                  </div>
                </div>
                {onClose && (
                  <Button
                    variant="light"
                    size="sm"
                    onClick={onClose}
                    className="d-flex align-items-center justify-content-center border rounded-circle"
                    style={{ width: "40px", height: "40px" }}
                    title="Close"
                  >
                    <FaTimes size={18} />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="intel-tabs px-4 bg-light border-bottom sticky-top"
            style={{ top: "68px", zIndex: 999 }}
          >
            {/* Tab 1: Business Identity & Contact */}
            <Tab eventKey="identity" title={<><FaBuilding /> Business Identity</>}>
              <div className="row g-0">
                {/* Left: Logo */}
                <div className="col-12 col-xl-5 bg-light border-end d-flex align-items-center justify-content-center p-5" style={{ minHeight: "500px" }}>
                  <div className="image-glow w-100 d-flex align-items-center justify-content-center" style={{ maxWidth: "500px" }}>
                    {!imageError && supplier.logo_image ? (
                      <img
                        src={supplier.logo_image}
                        alt={supplier.business_name}
                        className="img-fluid rounded-4 shadow-lg w-100"
                        style={{ objectFit: "contain", background: "#fff", padding: "20px", minHeight: "400px" }}
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div
                        className="rounded-4 shadow-lg d-flex align-items-center justify-content-center bg-primary text-white"
                        style={{
                          width: '400px',
                          height: '400px',
                          fontSize: '120px',
                          fontWeight: '800',
                          background: 'linear-gradient(135deg, #696cff 0%, #3f4191 100%)',
                          boxShadow: '0 20px 40px rgba(105, 108, 255, 0.25)'
                        }}
                      >
                        {getInitials(supplier.business_name)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Business Info */}
                <div className="col-12 col-xl-7 p-4 p-md-5 bg-white">
                  {/* Contact Information */}
                  <div className="mb-5">
                    <h5 className="fw-bold mb-3 d-flex align-items-center">
                      <FaPhone className="text-primary me-2" />
                      Contact Details
                    </h5>
                    <div className="row g-3">
                      <div className="col-sm-6">
                        <div className="spec-card">
                          <div className="details-label">Primary Contact</div>
                          <div className="details-value">{supplier.contact_person || "N/A"}</div>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="spec-card">
                          <div className="details-label">Phone Number</div>
                          <div className="details-value font-monospace">{supplier.contact_info?.contact_number || "N/A"}</div>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="spec-card">
                          <div className="details-label">Alternate Number</div>
                          <div className="details-value font-monospace">{supplier.contact_info?.alternate_contact_number || "N/A"}</div>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="spec-card">
                          <div className="details-label">
                            <FaEnvelope className="me-1" /> Email Address
                          </div>
                          <div className="details-value" style={{ fontSize: "0.9rem" }}>{supplier.contact_info?.email || "Not provided"}</div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="spec-card">
                          <div className="details-label">
                            <FaGlobe className="me-1" /> Website
                          </div>
                          <div className="details-value text-primary" style={{ fontSize: "0.9rem" }}>
                            {supplier.contact_info?.website ? (
                              <a href={supplier.contact_info.website} target="_blank" rel="noopener noreferrer">{supplier.contact_info.website}</a>
                            ) : "No website"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="mb-5">
                    <h5 className="fw-bold mb-3 d-flex align-items-center">
                      <FaMapMarkerAlt className="text-danger me-2" />
                      Business Address
                    </h5>
                    <div className="spec-card">
                      <div className="details-label">Full Address</div>
                      <div className="details-value lh-base">
                        {supplier.address?.address_line1}
                        {supplier.address?.address_line2 && <>, {supplier.address.address_line2}</>}
                        <br />
                        {supplier.address?.city}, {supplier.address?.state || "N/A"}
                        <br />
                        {supplier.address?.country} - {supplier.address?.postal_code || "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="mt-auto">
                    <h5 className="fw-bold mb-3">Additional Notes</h5>
                    <p className="text-muted lh-base" style={{ fontSize: "1rem", whiteSpace: "pre-line" }}>
                      {supplier.notes || "No additional notes available for this supplier."}
                    </p>
                  </div>
                </div>
              </div>
            </Tab>

            {/* Tab 2: Financial Overview - ENHANCED UI */}
            <Tab eventKey="financial" title={<><FaWallet /> Financial Overview</>}>
              <div className="p-4 p-md-5 bg-white">
                {/* Calculate metrics */}
                {(() => {
                  const currentBalance = liveAccount?.balance || supplier.account?.balance || supplier.financial?.current_balance || 0;
                  const creditLimit = supplier.financial?.credit_limit || 0;
                  const creditUsed = Math.abs(currentBalance);
                  const creditAvailable = Math.max(0, creditLimit - creditUsed);
                  const creditUtilization = creditLimit > 0 ? (creditUsed / creditLimit) * 100 : 0;
                  const isOverLimit = creditUsed > creditLimit && creditLimit > 0;

                  return (
                    <div className="row g-4">
                      {/* Main Balance Card with Dynamic Gradient */}
                      <div className="col-md-6">
                        <div
                          className="position-relative overflow-hidden rounded-4 shadow-lg p-4"
                          style={{
                            background: currentBalance < 0
                              ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                              : currentBalance > 0
                                ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)'
                                : 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                            color: 'white',
                            minHeight: '200px',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {/* Background Icon Watermark */}
                          <div className="position-absolute" style={{ top: '-20px', right: '-20px', opacity: 0.15 }}>
                            <FaWallet size={140} />
                          </div>

                          {/* Status Badge */}
                          <div className="mb-3">
                            <span className="badge bg-white bg-opacity-25 px-3 py-2 rounded-pill">
                              <small className="text-uppercase fw-bold" style={{ letterSpacing: '1px' }}>
                                Current Balance
                              </small>
                            </span>
                          </div>

                          {/* Balance Amount */}
                          <div className="position-relative">
                            <div className="display-4 fw-bold mb-2" style={{ lineHeight: '1.2' }}>
                              {currentBalance < 0 && '-'}Rs. {Math.abs(currentBalance).toLocaleString()}
                              <span className="fs-5">.00</span>
                            </div>

                            {/* Status Indicator */}
                            <div className="d-flex align-items-center gap-2 mt-3">
                              <div
                                className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px' }}
                              >
                                {currentBalance < 0 ? (
                                  <FaFileInvoiceDollar className="text-danger" size={16} />
                                ) : currentBalance > 0 ? (
                                  <FaHistory className="text-success" size={16} />
                                ) : (
                                  <FaWallet className="text-secondary" size={16} />
                                )}
                              </div>
                              <div>
                                <div className="fw-bold">
                                  {currentBalance < 0 ? "Amount Payable" : currentBalance > 0 ? "Credit Balance" : "Account Settled"}
                                </div>
                                <div className="small opacity-75">
                                  {currentBalance < 0
                                    ? "Payment pending to supplier"
                                    : currentBalance > 0
                                      ? "Supplier owes you"
                                      : "No outstanding dues"}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom Accent Line */}
                          <div
                            className="position-absolute bottom-0 start-0 w-100"
                            style={{
                              height: '4px',
                              background: 'rgba(255, 255, 255, 0.3)'
                            }}
                          />
                        </div>
                      </div>

                      {/* Credit Utilization Card */}
                      <div className="col-md-6">
                        <div className="spec-card h-100 position-relative overflow-hidden">
                          <div className="details-label mb-3">
                            <FaCreditCard className="me-1" /> Credit Utilization
                          </div>

                          {creditLimit > 0 ? (
                            <>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted small">Used: Rs. {creditUsed.toLocaleString()}</span>
                                <span className="fw-bold" style={{ color: isOverLimit ? '#dc3545' : '#696cff' }}>
                                  {creditUtilization.toFixed(1)}%
                                </span>
                              </div>

                              {/* Progress Bar */}
                              <div className="progress" style={{ height: '12px', borderRadius: '8px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${Math.min(creditUtilization, 100)}%`,
                                    backgroundColor: isOverLimit ? '#dc3545' : creditUtilization > 80 ? '#ffc107' : '#696cff',
                                    transition: 'width 0.3s ease'
                                  }}
                                  aria-valuenow={creditUtilization}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                />
                              </div>

                              <div className="d-flex justify-content-between mt-2">
                                <span className="small text-muted">Available: Rs. {creditAvailable.toLocaleString()}</span>
                                <span className="small text-muted">Limit: Rs. {creditLimit.toLocaleString()}</span>
                              </div>

                              {isOverLimit && (
                                <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small">
                                  ⚠️ Credit limit exceeded!
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-3 text-muted">
                              <p className="mb-0">No credit limit set</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Financial Metrics Grid */}
                      <div className="col-12">
                        <div className="row g-3">
                          {/* Opening Balance */}
                          <div className="col-md-3">
                            <div className="spec-card">
                              <div className="details-label">
                                <FaHistory className="me-1" /> Opening Balance
                              </div>
                              <div className="details-value text-success">
                                Rs. {(supplier.financial?.opening_balance || 0).toLocaleString()}
                              </div>
                            </div>
                          </div>

                          {/* Credit Limit */}
                          <div className="col-md-3">
                            <div className="spec-card">
                              <div className="details-label">
                                <FaCreditCard className="me-1" /> Credit Limit
                              </div>
                              <div className="details-value text-primary">
                                Rs. {(supplier.financial?.credit_limit || 0).toLocaleString()}
                              </div>
                            </div>
                          </div>

                          {/* Credit Period */}
                          <div className="col-md-3">
                            <div className="spec-card">
                              <div className="details-label">
                                <FaCalendarAlt className="me-1" /> Credit Period
                              </div>
                              <div className="details-value">
                                {supplier.financial?.credit_period || "N/A"}
                              </div>
                            </div>
                          </div>

                          {/* Payment Terms */}
                          <div className="col-md-3">
                            <div className="spec-card">
                              <div className="details-label">
                                <FaFileInvoiceDollar className="me-1" /> Payment Terms
                              </div>
                              <div className="details-value" style={{ fontSize: '0.9rem' }}>
                                {supplier.financial?.payment_terms || "N/A"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Account Summary Stats */}
                      <div className="col-12">
                        <div className="border rounded p-4 bg-light">
                          <h6 className="fw-bold mb-3">Account Summary</h6>
                          <div className="row g-3">
                            <div className="col-md-4">
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div className="rounded-circle bg-white d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                    <FaWallet className="text-primary" size={20} />
                                  </div>
                                </div>
                                <div>
                                  <div className="small text-muted">Total Outstanding</div>
                                  <div className="fw-bold text-danger">Rs. {creditUsed.toLocaleString()}</div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div className="rounded-circle bg-white d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                    <FaCreditCard className="text-success" size={20} />
                                  </div>
                                </div>
                                <div>
                                  <div className="small text-muted">Credit Available</div>
                                  <div className="fw-bold text-success">Rs. {creditAvailable.toLocaleString()}</div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div className="rounded-circle bg-white d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                    <FaCalendarAlt className="text-info" size={20} />
                                  </div>
                                </div>
                                <div>
                                  <div className="small text-muted">Payment Cycle</div>
                                  <div className="fw-bold">{supplier.financial?.credit_period || "N/A"}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bank Details */}
                      {supplier.bank_details && (supplier.bank_details.bank_name || supplier.bank_details.bank_account_number) && (
                        <div className="col-12">
                          <div className="border-top pt-4 mt-3">
                            <h5 className="fw-bold mb-4 d-flex align-items-center">
                              <FaBuilding className="text-primary me-2" />
                              Bank Account Details
                            </h5>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <div className="spec-card">
                                  <div className="details-label">Bank Name</div>
                                  <div className="details-value">{supplier.bank_details.bank_name || "N/A"}</div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="spec-card">
                                  <div className="details-label">Account Number</div>
                                  <div className="details-value font-monospace">{supplier.bank_details.bank_account_number || "N/A"}</div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="spec-card">
                                  <div className="details-label">Routing Number</div>
                                  <div className="details-value font-monospace">{supplier.bank_details.bank_routing_number || "N/A"}</div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="spec-card">
                                  <div className="details-label">SWIFT Code</div>
                                  <div className="details-value font-monospace">{supplier.bank_details.SWIFT_code || "N/A"}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </Tab>

            {/* Tab 3: Purchase History - WITH REAL DATA */}
            <Tab eventKey="purchases" title={<><FaFileInvoiceDollar /> Purchase History</>}>
              <div className="p-4">
                <BrandTable
                  title="Purchase Order History"
                  data={supplierDuePurchases}
                  columns={purchaseColumns}
                  loading={status === "loading"}
                  emptyMessage="No purchase orders found for this supplier."
                  emptyIcon={<FaShoppingCart size={40} className="mb-3 text-secondary opacity-50" />}
                  size="sm"
                  stripe={true}
                  pagination={true}
                  rowsPerPage={10}
                />
              </div>
            </Tab>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default SupplierDetails;
